import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { doGetStaffCounsels } from '../../../store/staffCounsel.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import moment from 'moment';

import { useSnackbar } from 'notistack';
import { Table, TableColumnsType } from 'antd';

import config from '../../../lib/config';
import Pagination from '../../Pagination';
import { getCommonCodeLabel } from '../../../lib/store';
import InputCounselModal from '../../modals/InputCounselModal';
import { errorVariant, warningVariant } from '../../../lib/barVariant';
import Loader from '../../Loader';

interface IProps {
  memberCode: string;
}

const StaffCounsel: FC<IProps> = ({ memberCode }) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const dispatch = useDispatch();
  const { staffCounsels, isLoading } = useSelector(
    (state: RootState) => state.staffCounsel,
  );

  const [counselPage, setCounselPage] = useState(1);
  const [counselType, setCounselType] = useState<string>('');
  const [inputCounselVisible, setInputCounselVisible] = useState(false);
  const [counselTitle, setCounselTitle] = useState('');
  const [counselContent, setCounselContent] = useState('');

  useEffect(() => {
    getStaffCounsels();
  }, []);

  const getStaffCounsels = async () => {
    const res = await dispatch(doGetStaffCounsels(memberCode));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  const counselColumns: TableColumnsType<any> = [
    {
      title: t('campus.staff.column9'),
      dataIndex: 'counselGbn',
      key: 'counselGbn',
      align: 'center',
      width: 110,
      sorter: (a, b) => a.counselGbn?.localeCompare(b.counselGbn),
      // MB0004 = 상담 유형
      render: (value: string) => getCommonCodeLabel('MB0008', value, i18n.language),
    },
    {
      title: t('common.column.title'),
      dataIndex: 'counselTitle',
      key: 'counselTitle',
      sorter: (a, b) => a.counselTitle?.localeCompare(b.counselTitle),
      render: (value: string) => <div className='text-left'>{value}</div>,
      align: 'center',
    },
    {
      title: t('campus.staff.column10'),
      dataIndex: 'counselContent',
      key: 'counselContent',
      sorter: (a, b) => a.counselContent?.localeCompare(b.counselContent),
      render: (value: string) => {
        return (
          <div className='text-left'>
            {
              value.length > config.counselLength
              ? `${value.substring(0, config.counselLength)}...`
              : value 
            }
          </div>
        )
      },
      align: 'center',
    },
    {
      title: t('campus.staff.column11'),
      dataIndex: 'regDate',
      key: 'regDate',
      align: 'center',
      sorter: (a, b) => a.regDate?.localeCompare(b.regDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('campus.staff.column12'),
      dataIndex: 'studentKorName',
      key: 'studentKorName',
      align: 'center',
      sorter: (a, b) => a.studentKorName?.localeCompare(b.studentKorName),
      render: (value, record) => i18n.language === 'ko' ? value : (record.studentEngName ?? '-')
    },
  ];

  return (
    <div>
      {isLoading ? <Loader /> : null}
      <Table
        className='comm-table__area'
        columns={counselColumns}
        dataSource={staffCounsels || undefined}
        pagination={{
          current: counselPage,
          pageSize: config.pageSize,
        }}
        onRow={(record) => {
          return {
            onClick: () => {
              setCounselTitle(record.counselTitle);
              setCounselContent(record.counselContent);
              setCounselType(record.counselTargetGbn);
              setInputCounselVisible(true);
            },
          };
        }}
        rowClassName="pointer"
      />
      <div className="comm-bx__between">
        <div className="common-paging center">
          <Pagination
            count={staffCounsels?.length || 0}
            page={counselPage}
            setPage={setCounselPage}
          />
        </div>
      </div>

      {/* 상담 확인 모달 */}
      <InputCounselModal
        inputCounselVisible={inputCounselVisible}
        cancelInputCounsel={() => {
          setCounselTitle('');
          setCounselContent('');
          setCounselType('');
          setInputCounselVisible(false);
        }}
        counselTitle={counselTitle}
        setCounselTitle={setCounselTitle}
        counselContent={counselContent}
        setCounselContent={setCounselContent}
        counselType={counselType}
        setCounselType={setCounselType}
        isDisabled
      />
    </div>
  );
};

export default StaffCounsel;
